/**
 * ActionRiskCategoriesPage — Motor Neutro D2 · «Risco por Ação» (casa de config da A3).
 *
 * Rota: /admin/ia/risco-de-acao. Consome o módulo `action-risk-category` do Engine.
 * Padrão D-B (defense-in-depth): gate próprio na página (`action-risk-category-read`), além
 * da query gateada no hook — não confia só na rota/nav. Autoria gateada em
 * `action-risk-category-author` e aprovação em `action-risk-category-platform-approve`
 * dentro da view (sem elas, leitura).
 *
 * ⚠️ O seletor de vertical é `PlaybookVertical` (eixo FUNCIONAL, o do gate) — NÃO o segmento
 * do tenant. Enquanto não houver leitura dos verticais dos playbooks do tenant nesta tela, o
 * seletor oferece o universo canônico do enum, com a base agnóstica primeiro.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { ActionRiskCategoriesView } from '../features/action-risk-categories/ActionRiskCategoriesView';
import { useResolvedActionRiskCategories } from '../features/action-risk-categories/use-action-risk-categories';
import { useCapability } from '../hooks/use-capability';

import '../features/action-risk-categories/action-risk-categories.css';

/**
 * Universo FUNCIONAL (`PlaybookVertical`). `null` primeiro: é o baseline vertical-agnóstico e
 * a resposta honesta quando o piso não depende do vertical.
 */
const VERTICAL_OPTIONS: readonly (string | null)[] = [
  null,
  'support',
  'commercial',
  'retention',
  'collections',
  'triage',
  'onboarding',
  'satisfaction_survey',
  'legal',
  'healthcare',
  'other',
];

export function ActionRiskCategoriesPage(): JSX.Element {
  const canRead = useCapability('action-risk-category-read');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="action-risk-screen action-risk-screen--gate">
        <CapabilityLockedBanner
          capability="action-risk-category-read"
          tierMin="solo"
          fallbackMessage="A configuração de risco por ação é restrita a perfis com acesso às configurações do tenant."
        />
      </div>
    );
  }

  return <ActionRiskCategoriesPageInner />;
}

function ActionRiskCategoriesPageInner(): JSX.Element {
  const [vertical, setVertical] = useState<string | null>(null);
  const canAuthor = useCapability('action-risk-category-author');
  const canApprove = useCapability('action-risk-category-platform-approve');
  const query = useResolvedActionRiskCategories(vertical);

  if (query.isLoading) {
    return (
      <div className="action-risk-screen">
        <div className="arc-page">
          <p className="arc-muted">Carregando as ações do motor…</p>
        </div>
      </div>
    );
  }

  if (query.isError || query.data === null) {
    return (
      <div className="action-risk-screen">
        <div className="arc-page">
          <p className="arc-banner arc-banner--muted" role="alert">
            Falha ao carregar a configuração de risco por ação.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ActionRiskCategoriesView
      catalog={query.data}
      verticalOptions={VERTICAL_OPTIONS}
      vertical={vertical}
      onVerticalChange={setVertical}
      canAuthor={canAuthor}
      canApprove={canApprove}
    />
  );
}
